/** Small, allocation-free math helpers shared by every technique. */

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export interface Vec2 {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Inverse lerp; returns 0 when the range is degenerate instead of NaN/Infinity. */
export const invLerp = (a: number, b: number, v: number): number => (b === a ? 0 : (v - a) / (b - a));

export const remap = (v: number, inA: number, inB: number, outA: number, outB: number): number =>
  lerp(outA, outB, invLerp(inA, inB, v));

export const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = clamp01(invLerp(edge0, edge1, x));
  return t * t * (3 - 2 * t);
};

/** Mixes hue-like values correctly across the wrap point. */
export const lerpAngle = (a: number, b: number, t: number): number => {
  let d = ((b - a) % TAU + TAU + Math.PI) % TAU - Math.PI;
  return a + d * t;
};

export const dist = (ax: number, ay: number, bx: number, by: number): number =>
  Math.hypot(ax - bx, ay - by);

export const dist2 = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};

/** Euclidean modulo — always returns a value in [0, n). */
export const mod = (a: number, n: number): number => ((a % n) + n) % n;

export const rectUnion = (a: Rect, b: Rect): Rect => {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x + a.width, b.x + b.width);
  const y2 = Math.max(a.y + a.height, b.y + b.height);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
};

export const emptyRect = (): Rect => ({
  x: Infinity,
  y: Infinity,
  width: -Infinity,
  height: -Infinity,
});

export const rectFromPoints = (points: ArrayLike<number>): Rect => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/** 2D affine matrix in the same component order as CanvasRenderingContext2D.transform(). */
export interface Mat2D {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const matIdentity = (): Mat2D => ({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

/** Returns m1 * m2 — i.e. m2 is applied first, then m1. */
export const matMul = (m1: Mat2D, m2: Mat2D): Mat2D => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f,
});

export const matTranslate = (tx: number, ty: number): Mat2D => ({ a: 1, b: 0, c: 0, d: 1, e: tx, f: ty });

export const matScale = (sx: number, sy: number): Mat2D => ({ a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 });

export const matRotate = (rad: number): Mat2D => {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { a: c, b: s, c: -s, d: c, e: 0, f: 0 };
};

/** Shear; angles in radians. skewX tilts along x as y grows, skewY vice versa. */
export const matSkew = (skewX: number, skewY: number): Mat2D => ({
  a: 1,
  b: Math.tan(skewY),
  c: Math.tan(skewX),
  d: 1,
  e: 0,
  f: 0,
});

export const applyMat = (m: Mat2D, x: number, y: number, out: Vec2): Vec2 => {
  out.x = m.a * x + m.c * y + m.e;
  out.y = m.b * x + m.d * y + m.f;
  return out;
};

/** Average absolute scale factor of a matrix — used to keep flattening tolerance in output pixels. */
export const matScaleMagnitude = (m: Mat2D): number => {
  const sx = Math.hypot(m.a, m.b);
  const sy = Math.hypot(m.c, m.d);
  return (sx + sy) / 2;
};
